import './App.css';
import { useEffect, useState, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db, app, getMessaging, isMessagingSupported } from './firebase';
import { getToken } from 'firebase/messaging';
import {
  playHubtuneNotificationSound,
  unlockHubtuneNotificationSound,
} from './utils/hubtuneNotificationSound';
import { getUserById } from './services/userService';
import SignUp from './components/SignUp';
import ErrorBoundary from './components/ErrorBoundary';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getAnalyticsInstance } from './firebase';
import { logEvent } from 'firebase/analytics';
import SearchUsers from './components/SearchUsers';
import UsersList from './components/UsersList';
import ProfilePage from './components/ProfilePage';
import MyProfile from './components/MyProfile';
import ProfileCard from './components/ProfileCard.jsx';
import SearchResultsPage from './components/SearchResultsPage';
import Wallet from './pages/Wallet.jsx';
import InstallPrompt from './components/InstallPrompt';
import Alerts from './components/Alerts.jsx';
import SetupProfile from './pages/SetupProfile.jsx';
import ArtistUpiSetup from './pages/ArtistUpiSetup.jsx';
import NameEntry from './pages/NameEntry.jsx';
import RoleSelection from './pages/RoleSelection.jsx';
import SongFeed from './pages/SongFeed.jsx';
import Messages from './components/Messages';
import About from './pages/About.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import MusicRights from './pages/MusicRights.jsx';
import Refund from './pages/Refund.jsx';
import Contact from './pages/Contact.jsx';
import ArtistPayoutKycPolicy from './pages/ArtistPayoutKycPolicy.jsx';
import AdminPanel from './pages/AdminPanel.jsx';
import SongDetail from './pages/SongDetail.jsx';
import ProfilePhotoSetup from './components/ProfilePhotoSetup';
import ProfileEditPage from './pages/ProfileEditPage.jsx';

// Component to check if user has profile before showing home - render immediately
function HomeWithProfileCheck({ user }) {
  const [profileStatus, setProfileStatus] = useState('checking');
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      try {
        const profile = await getUserById(user.uid);
        if (!cancelledRef.current) {
          if (!profile?.name?.trim()) {
            setProfileStatus('missing-name');
          } else if (!profile.role) {
            sessionStorage.setItem('setupDisplayName', profile.name.trim());
            setProfileStatus('missing-role');
          } else {
            setProfileStatus('complete');
          }
        }

      } catch (err) {
        console.error('Error checking profile:', err);
        if (!cancelledRef.current) {
          setProfileStatus('missing-name');
        }
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [user.uid]);

  if (profileStatus === 'checking') {
    return <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center">Loading profile...</div>;
  }

  if (profileStatus === 'missing-name') {
    return <Navigate to="/setup-name" replace />;
  }
  if (profileStatus === 'missing-role') {
    return <Navigate to="/select-role" replace />;
  }

  return <SongFeed />;
}

function ProfilePhotoSetupRoute({ user }) {
  const navigate = useNavigate();
  return (
    <ProfilePhotoSetup
      user={user}
      onComplete={() => navigate('/', { replace: true })}
      onBack={() => navigate('/select-role', { replace: true })}
    />
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [paymentCongratulations, setPaymentCongratulations] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setCurrentUser(u || null);
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unlock = () => unlockHubtuneNotificationSound();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    let initialSnapshot = true;
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid)
    );
    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      if (initialSnapshot) {
        initialSnapshot = false;
        return;
      }
      const badgeChange = snapshot.docChanges().find(({ type, doc }) =>
        type === 'added' && doc.data()?.type === 'badge_earned'
      );
      const hasPaymentAlert = snapshot.docChanges().some(({ type, doc }) =>
        type === 'added' && ['tip_confirmed', 'badge_earned'].includes(doc.data()?.type)
      );
      if (hasPaymentAlert && typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([180, 80, 180, 80, 350]);
      }
      if (hasPaymentAlert) {
        playHubtuneNotificationSound();
      }
      if (badgeChange) {
        setPaymentCongratulations(badgeChange.doc.data().message);
        window.setTimeout(() => setPaymentCongratulations(null), 6000);
      }
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
      ) {
        snapshot.docChanges()
          .filter(({ type }) => type === 'added')
          .forEach(({ doc }) => {
            const data = doc.data();
            new Notification('HubTune', {
              body: data.message || 'You have a new notification.',
              tag: doc.id,
            });
          });
      }
    }, (error) => {
      console.error('Failed to subscribe to payment alerts:', error);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const paymentQueries = [
      query(collection(db, 'transactions'), where('senderId', '==', currentUser.uid)),
      query(collection(db, 'transactions'), where('receiverId', '==', currentUser.uid)),
    ];
    const seen = new Set();
    const initialized = [false, false];
    const unsubscribers = paymentQueries.map((paymentQuery, index) => onSnapshot(paymentQuery, (snapshot) => {
      if (!initialized[index]) {
        snapshot.docs.forEach((transactionDoc) => seen.add(transactionDoc.id));
        initialized[index] = true;
        return;
      }
      snapshot.docChanges().forEach(({ type, doc: transactionDoc }) => {
        const transaction = transactionDoc.data();
        if (type === 'added' && transaction.status === 'confirmed' && !seen.has(transactionDoc.id)) {
          seen.add(transactionDoc.id);
          if (navigator.vibrate) navigator.vibrate([180, 80, 180, 80, 350]);
          playHubtuneNotificationSound();
        }
        if (type === 'modified' && transaction.status === 'confirmed' && !seen.has(transactionDoc.id)) {
          seen.add(transactionDoc.id);
          if (navigator.vibrate) navigator.vibrate([180, 80, 180, 80, 350]);
          playHubtuneNotificationSound();
        }
      });
    }, (error) => {
      console.error('Failed to subscribe to confirmed payments:', error);
    }));

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [currentUser?.uid]);

  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      alert('Browser site settings खोलकर HubTune notifications को Allow करें, फिर page refresh करें.');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        await registerPushToken(currentUser.uid);
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  };

  const registerPushToken = async (uid) => {
    if (!uid || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (!(await isMessagingSupported())) return;
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      updateViaCache: 'none',
    });
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: 'BHilphJapetzBBB70Vlg1RJPxZiwvuP6bRjny7wpPEtDdk0w1PSD5ERJIm2mCQg0mw4lLe6zOQKUVSAXFZ8Vy3k',
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await setDoc(doc(db, 'users', uid, 'fcmTokens', token), {
        token,
        updatedAt: serverTimestamp(),
        platform: navigator.userAgent,
      }, { merge: true });
    }
  };

  useEffect(() => {
    if (currentUser?.uid && notificationPermission === 'granted') {
      registerPushToken(currentUser.uid).catch((error) => {
        console.error('Failed to register background notifications:', error);
      });
    }
  }, [currentUser?.uid, notificationPermission]);

  // Show minimal loading only if auth not checked yet
  if (!authChecked) {
    return (
      <div className="App" style={{ color: '#fff', background: '#0e0e0e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Checking session...
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        {currentUser && notificationPermission !== 'granted' && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[110] w-[min(92vw,30rem)] rounded-2xl border border-red-500/50 bg-[#171717] px-4 py-3 text-white shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold">
                  {notificationPermission === 'denied' ? 'Notifications blocked हैं' : 'Notifications on करें'}
                </div>
                <div className="text-xs text-white/60">
                  {notificationPermission === 'denied'
                    ? 'Browser site settings में Notifications को Allow करें।'
                    : notificationPermission === 'unsupported'
                      ? 'इस browser में notifications supported नहीं हैं।'
                      : 'Tips, badges और messages का alert तुरंत मिलेगा।'}
                </div>
              </div>
              {notificationPermission !== 'unsupported' && (
                <button
                  type="button"
                  onClick={requestNotificationPermission}
                  className="shrink-0 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold"
                >
                  {notificationPermission === 'denied' ? 'How to allow' : 'Allow'}
                </button>
              )}
            </div>
          </div>
        )}
        {paymentCongratulations && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[min(92vw,28rem)] rounded-2xl border border-yellow-400/50 bg-[#17120a] px-5 py-4 text-center text-white shadow-2xl">
            <div className="text-3xl mb-1">🏅</div>
            <div className="font-bold text-yellow-300">Congratulations!</div>
            <div className="text-sm mt-1">{paymentCongratulations}</div>
          </div>
        )}
        <RouteChangeTracker />
        <nav className="top-nav">
          <Link to="/">Home</Link>
          <Link to="/search">Search</Link>
          <Link to="/all-users">All Users</Link>
          <Link to="/me">Profile</Link>
          <Link to="/alerts">Alerts</Link>
        </nav>
        <div className="app-container">
          <ErrorBoundary>
            <Routes>
              {/* Home: if logged in show feed, else redirect to login */}
              <Route
                path="/"
                element={
                  currentUser ? (
                    <HomeWithProfileCheck user={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Login/Signup: if already logged in, go home */}
              <Route
                path="/login"
                element={
                  currentUser ? (
                    <Navigate to="/" replace />
                  ) : (
                    <SignUp onComplete={() => { /* after signup we rely on auth state to redirect */ }} />
                  )
                }
              />

              <Route path="/search" element={currentUser ? <SearchUsers /> : <Navigate to="/login" replace />} />
              <Route path="/results" element={currentUser ? <SearchResultsPage /> : <Navigate to="/login" replace />} />
              <Route path="/all-users" element={currentUser ? <UsersList /> : <Navigate to="/login" replace />} />
              <Route path="/profile/:uid" element={currentUser ? <ProfilePage /> : <Navigate to="/login" replace />} />
              {/* support /profile (no uid) by redirecting to /me which normalizes to /profile/:uid */}
              <Route path="/profile" element={<Navigate to="/me" replace />} />
              <Route path="/me" element={currentUser ? <MyProfile /> : <Navigate to="/login" replace />} />
              {/* Minimal PWA-style profile card using Firestore realtime */}
              <Route path="/pwa-profile" element={currentUser ? <ProfileCard /> : <Navigate to="/login" replace />} />
              <Route path="/wallet/:uid" element={currentUser ? <Wallet /> : <Navigate to="/login" replace />} />
              <Route path="/alerts" element={currentUser ? <Alerts /> : <Navigate to="/login" replace />} />
              <Route path="/messages" element={currentUser ? <Messages /> : <Navigate to="/login" replace />} />
              <Route path="/song-feed" element={currentUser ? <SongFeed /> : <Navigate to="/login" replace />} />
              <Route path="/song/:id" element={currentUser ? <SongDetail /> : <Navigate to="/login" replace />} />
              <Route path="/setup-profile" element={currentUser ? <SetupProfile /> : <Navigate to="/login" replace />} />
              <Route path="/setup-name" element={currentUser ? <NameEntry /> : <Navigate to="/login" replace />} />
              <Route path="/select-role" element={currentUser ? <RoleSelection /> : <Navigate to="/login" replace />} />
              <Route path="/setup-photo" element={currentUser ? <ProfilePhotoSetupRoute user={currentUser} /> : <Navigate to="/login" replace />} />
              <Route path="/edit-profile" element={currentUser ? <ProfileEditPage user={currentUser} /> : <Navigate to="/login" replace />} />
              <Route path="/setup-artist-upi" element={currentUser ? <ArtistUpiSetup /> : <Navigate to="/login" replace />} />

              {/* Static pages - accessible without login */}
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/music-rights" element={<MusicRights />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/artist-payout-kyc-policy" element={<ArtistPayoutKycPolicy />} />

              {/* Admin Panel - Only accessible by admin email */}
              <Route path="/admin" element={<AdminPanel />} />

              {/* Fallback: redirect any unknown route based on auth */}
              <Route path="*" element={<Navigate to={currentUser ? '/' : '/login'} replace />} />
            </Routes>
          </ErrorBoundary>
        </div>
        {/* GlobalAudioPlayer removed - user requested to remove bottom audio player */}
        {/* <GlobalAudioPlayer /> */}
        {/* Install Prompt - Shows immediately when site opens */}
        <InstallPrompt />
      </div>
    </BrowserRouter>
  );
}

export default App;

// Tracks route changes as page_view events for Firebase Analytics (SPA)
function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    try {
      const analytics = getAnalyticsInstance();
      if (analytics) {
        logEvent(analytics, 'page_view', {
          page_title: document?.title || '',
          page_location: window?.location?.href || '',
          page_path: location?.pathname || '',
        });
      }
    } catch (_) {
      // no-op
    }
  }, [location]);
  return null;
}
